import jsPDF from 'jspdf';
import TrripLogo from '../assets/Trrip_logo/Trrip_logo_1.png';
import { formatDate } from './formatters';

export const SUPPLIER_LEGAL_DETAILS = {
  legalName: 'ORBITRA TECHNOLOGIES LLP',
  gstin: '27AAJFO3521B1ZN',
  address: 'Shop no. 3, Gokul Plaza, Veer Savarkar Road, Nr RLY Phatak Gawad Wadi, Virar East, Vasai Virar, Palghar, Maharashtra - 401303',
  supportEmail: 'support@trrip.live'
};

export const getPaymentMethodLabel = (method) => {
  if (!method) return 'CARD';
  const m = method.toLowerCase();
  if (m === 'subscription' || m === 'card') return 'CARD';
  if (m === 'upi') return 'UPI';
  return m.substring(0, 4).toUpperCase();
};

export const getFyInvoiceNo = (dateInput, seqNum = 1) => {
  const d = new Date(dateInput || Date.now());
  const year = d.getFullYear();
  const month = d.getMonth();
  let startYr, endYr;
  if (month >= 3) {
    startYr = year % 100;
    endYr = (year + 1) % 100;
  } else {
    startYr = (year - 1) % 100;
    endYr = year % 100;
  }
  const fyPrefix = `${String(startYr).padStart(2, '0')}${String(endYr).padStart(2, '0')}`;
  const seqStr = String(seqNum).padStart(3, '0');
  return `${fyPrefix}${seqStr}`;
};

export const generateInvoiceDoc = async (pm, formData = {}, subscription = null, paymentHistory = []) => {
  const doc = new jsPDF();
  const status = (pm.status || 'success').toLowerCase();
  const isSuccess = status === 'success' || status === 'paid';
  const isFailed = status === 'failed';

  const chronologicalIndex = [...paymentHistory].reverse().findIndex((p) => p._id === pm._id);
  const seqNum = chronologicalIndex >= 0 ? chronologicalIndex + 1 : 1;

  const invoiceNo = pm.invoice_number || getFyInvoiceNo(pm.paid_at || pm.paidAt || pm.createdAt, seqNum);
  const invoiceDate = formatDate(pm.paid_at || pm.paidAt || pm.createdAt);

  const planName =
    pm.plan_name ||
    pm.subscription_id?.plan_id?.name ||
    pm.plan_id?.name ||
    subscription?.plan_id?.name ||
    'Growth';
  const totalAmount = pm.total_amount || pm.amount || 0;
  const baseAmount = pm.base_amount || (totalAmount > 0 ? totalAmount / 1.18 : 0);
  const gstAmount = pm.gst_amount || totalAmount - baseAmount;
  const cgst = pm.cgst_amount !== undefined && pm.cgst_amount > 0 ? pm.cgst_amount : gstAmount / 2;
  const sgst = pm.sgst_amount !== undefined && pm.sgst_amount > 0 ? pm.sgst_amount : gstAmount / 2;

  // Customer Details
  const custDetails = pm.customer_details || {};
  const custLegalName =
    custDetails.legal_name ||
    custDetails.name ||
    formData.gst_legal_name ||
    formData.business_name ||
    formData.owner_name ||
    'Trrip Partner Agent';
  const custAddress =
    custDetails.address || formData.gst_address || formData.address || 'Address not provided';
  const custCity = custDetails.city || formData.city || '';
  const custPincode = custDetails.pincode || formData.pincode || '';
  const custEmail = custDetails.email || formData.email || formData.invoice_email || 'N/A';
  const custGstin = custDetails.gstin || formData.gstin || '';

  const suppDetails = pm.supplier_details || {};
  const suppLegalName = suppDetails.legal_name || SUPPLIER_LEGAL_DETAILS.legalName;
  const suppGstin = suppDetails.gstin || SUPPLIER_LEGAL_DETAILS.gstin;
  const suppEmail = suppDetails.email || SUPPLIER_LEGAL_DETAILS.supportEmail;

  // GST Split Rule: No GSTIN -> CGST+SGST, GSTIN starts with '27' -> CGST+SGST, Other GSTIN -> IGST
  const cleanGstin = (custGstin || '').trim();
  const isSameState =
    pm.is_same_state !== undefined ? pm.is_same_state : !cleanGstin || cleanGstin.startsWith('27');

  // 1. Sleek Teal Top Header Banner (Height: 24mm)
  doc.setFillColor(6, 121, 120); // #067978
  doc.rect(0, 0, 210, 24, 'F');

  // Draw Logo inside Teal Banner (Proportional Natural Aspect Ratio - No Stretch)
  try {
    const img = new Image();
    img.src = TrripLogo;
    await new Promise((res) => {
      img.onload = res;
      img.onerror = res;
    });
    if (img.complete && (img.naturalWidth || img.width) > 0) {
      const nw = img.naturalWidth || img.width;
      const nh = img.naturalHeight || img.height;
      const canvas = document.createElement('canvas');
      canvas.width = nw;
      canvas.height = nh;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const dataUrl = canvas.toDataURL('image/png');

      const maxW = 34;
      const maxH = 12;
      const aspect = nw / nh;

      let drawW = maxW;
      let drawH = drawW / aspect;
      if (drawH > maxH) {
        drawH = maxH;
        drawW = drawH * aspect;
      }

      const drawY = (24 - drawH) / 2;
      doc.addImage(dataUrl, 'PNG', 15, drawY, drawW, drawH);
    }
  } catch (e) {
    console.warn('Logo embed error:', e);
  }

  // Title & Badge on Right side of Header Banner
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(255, 255, 255);
  doc.text(isFailed ? 'PAYMENT RECEIPT' : 'TAX INVOICE', 195, 13, { align: 'right' });

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.text(isFailed ? 'TRANSACTION FAILED' : 'ORIGINAL FOR RECIPIENT', 195, 19, { align: 'right' });

  // 2. Metadata Section (Invoice #, Date, Ref ID, Paid Status)
  doc.setFillColor(248, 250, 252);
  doc.rect(15, 30, 180, 20, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.rect(15, 30, 180, 20, 'S');

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(100, 116, 139);
  doc.text('INVOICE NO:', 22, 38);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(30, 41, 59);
  doc.text(invoiceNo, 46, 38);

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(100, 116, 139);
  doc.text('INVOICE DATE:', 22, 45);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(30, 41, 59);
  doc.text(invoiceDate, 48, 45);

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(100, 116, 139);
  doc.text('PAYMENT REF:', 105, 38);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(30, 41, 59);
  doc.text(pm.razorpay_payment_id || pm.razorpayPaymentId || 'N/A', 133, 38);

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(100, 116, 139);
  doc.text('PAYMENT STATUS:', 105, 45);
  doc.setFont('helvetica', 'bold');
  if (isSuccess) {
    doc.setTextColor(16, 185, 129);
    doc.text('PAID SUCCESSFUL', 140, 45);
  } else if (isFailed) {
    doc.setTextColor(225, 29, 72);
    doc.text('FAILED', 140, 45);
  } else {
    doc.setTextColor(217, 119, 6);
    doc.text('PENDING', 140, 45);
  }

  // 3. Two-Column Supplier vs Recipient Details
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(148, 163, 184);
  doc.text('ISSUED BY (SUPPLIER)', 15, 60);
  doc.text('BILL TO (RECIPIENT)', 110, 60);

  doc.setDrawColor(226, 232, 240);
  doc.line(15, 63, 195, 63);

  // Supplier Details
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text(suppLegalName, 15, 70);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(71, 85, 105);
  doc.text('Shop no. 3, Gokul Plaza, Veer Savarkar Road,', 15, 76);
  doc.text('Nr RLY Phatak Gawad Wadi, Virar East,', 15, 82);
  doc.text('Vasai Virar, Palghar, Maharashtra - 401303', 15, 88);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(6, 121, 120);
  doc.text(`GSTIN: ${suppGstin}`, 15, 94);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(71, 85, 105);
  doc.text(`Email: ${suppEmail}`, 15, 100);

  // Recipient Details
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text(custLegalName, 110, 70);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(71, 85, 105);
  doc.text(custAddress, 110, 76);
  let recipientY = 82;
  if (custCity || custPincode) {
    doc.text(`${custCity} ${custPincode ? '- ' + custPincode : ''}`, 110, recipientY);
    recipientY += 6;
  }
  doc.text(`Email: ${custEmail}`, 110, recipientY);
  recipientY += 6;
  if (custGstin) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(6, 121, 120);
    doc.text(`GSTIN: ${custGstin.toUpperCase()}`, 110, recipientY);
  }

  // 4. Line Items Table
  const tableTop = 112;
  doc.setFillColor(6, 121, 120);
  doc.rect(15, tableTop, 180, 9, 'F');

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('DESCRIPTION', 20, tableTop + 6);
  doc.text('SAC CODE', 88, tableTop + 6);
  doc.text('TAX TYPE', 118, tableTop + 6);
  doc.text('BASE PRICE (INR)', 156, tableTop + 6, { align: 'right' });
  doc.text('TOTAL (INR)', 190, tableTop + 6, { align: 'right' });

  // Table Row
  doc.setFillColor(255, 255, 255);
  doc.rect(15, tableTop + 9, 180, 13, 'F');
  doc.setDrawColor(241, 245, 249);
  doc.rect(15, tableTop + 9, 180, 13, 'S');

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text(`Trrip ${planName} Plan Subscription`, 20, tableTop + 17);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(71, 85, 105);
  doc.text('998314', 88, tableTop + 17);
  doc.text(isSameState ? '18% CGST+SGST' : '18% IGST', 118, tableTop + 17);
  doc.text(baseAmount.toFixed(2), 156, tableTop + 17, { align: 'right' });
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(15, 23, 42);
  doc.text(totalAmount.toFixed(2), 190, tableTop + 17, { align: 'right' });

  // 5. Tax Summary Calculation Card
  const summaryTop = tableTop + 28;
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(110, summaryTop, 85, 42, 3, 3, 'F');
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(110, summaryTop, 85, 42, 3, 3, 'S');

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(71, 85, 105);
  doc.text('Taxable Value (Base Price):', 115, summaryTop + 10);
  doc.text(`INR ${baseAmount.toFixed(2)}`, 190, summaryTop + 10, { align: 'right' });

  if (isSameState) {
    doc.text('CGST (9%):', 115, summaryTop + 17);
    doc.text(`INR ${cgst.toFixed(2)}`, 190, summaryTop + 17, { align: 'right' });

    doc.text('SGST (9%):', 115, summaryTop + 24);
    doc.text(`INR ${sgst.toFixed(2)}`, 190, summaryTop + 24, { align: 'right' });
  } else {
    doc.text('IGST (18%):', 115, summaryTop + 20);
    doc.text(`INR ${gstAmount.toFixed(2)}`, 190, summaryTop + 20, { align: 'right' });
  }

  doc.setDrawColor(203, 213, 225);
  doc.line(115, summaryTop + 28, 190, summaryTop + 28);

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(6, 121, 120);
  doc.text('Total Invoice Amount:', 115, summaryTop + 36);
  doc.text(`INR ${totalAmount.toFixed(2)}`, 190, summaryTop + 36, { align: 'right' });

  // 6. Footer Disclaimer
  const footerY = 270;
  doc.setDrawColor(226, 232, 240);
  doc.line(15, footerY - 5, 195, footerY - 5);

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(148, 163, 184);
  doc.text(
    'This is an electronically generated tax invoice. No physical signature is required under IT Act 2000.',
    105,
    footerY,
    { align: 'center' }
  );
  doc.text(
    `For billing support or questions, reach out to ${suppEmail} | www.trrip.live`,
    105,
    footerY + 4,
    { align: 'center' }
  );

  return { doc, invoiceNo };
};
